import SimpleTableItem from '@/models/general/simpleTableItem'
import vueI18n from '@plugins/vue-i18n'
import dayjs from '@/services/date'
import DateFormatToken from '@/constants/date/DateFormatToken'

const { hmm, HHmm, HHmmss, Hmm, Hmmss, hmma } = DateFormatToken
const timeFormats = [hmm, HHmm, HHmmss, Hmm, Hmmss]

const FIELDS = ['start', 'end', 'breakStartTime', 'breakEndTime']
const FIELD_LABELS = {
  start: 'start',
  end: 'end',
  breakStartTime: 'break start',
  breakEndTime: 'break end',
}

const parseTime = (str) => dayjs(str, HHmm)

const formatTime = (str) => {
  if (!str || str.trim() === '') return ''
  return dayjs(str, timeFormats).format(HHmm)
}

const text = (key, ...args) =>
  vueI18n.t(`client.schedule.validation.${key}`, args)

const assertOrMsg = (condition, key, ...args) => condition || text(key, ...args)

export default class StandardScheduleTableItem extends SimpleTableItem {
  /**
   * Represents a single row/item in a standard schedule table.
   * Used for managing and validating schedule times (start, end, breaks).
   * Inherits from SimpleTableItem for base table item functionality.
   * @param {string} name - The name of the day of the week (e.g., "monday")
   * @param {string} start - The start time in "HH:mm" format (e.g., "09:00")
   * @param {string} end - The end time in "HH:mm" format (e.g., "17:00")
   * @param {string} breakStartTime - The break start time in "HH:mm" format (e.g., "12:30")
   * @param {string} breakEndTime - The break end time in "HH:mm" format (e.g., "13:00")
   * @param {boolean} removed - Whether the day is closed or not
   * @param {boolean} activeField - Tracks which field to show validation for if multiple fields are invalid
   */
  constructor({
    name,
    start = null,
    end = null,
    breakStartTime = null,
    breakEndTime = null,
    removed = false,
    activeField = null,
  } = {}) {
    super(removed)
    this.name = name
    this.shortName = name.slice(0, 3)
    this.start = formatTime(start)
    this.end = formatTime(end)
    this.breakStartTime = formatTime(breakStartTime)
    this.breakEndTime = formatTime(breakEndTime)
    this.activeField = activeField
  }

  validate(field, v, minStart = parseTime('03:00')) {
    if (this.removed) return true

    const res = _validate.call(this, field, v, minStart)

    if (res === true) return true
    return field === this.firstInvalidField ? res : true
  }

  /** first invalid field, preferring the field the user just edited */
  get firstInvalidField() {
    const order = this.activeField
      ? [this.activeField, ...FIELDS.filter((f) => f !== this.activeField)]
      : FIELDS

    for (const f of order) {
      const result = getFieldValidationResult(f, this, parseTime('03:00'))
      if (typeof result === 'string') return f
    }
    return null
  }
}

/**
 * Validates the schedule times for a given field
 * @param {string} field - The field to validate (e.g., "start", "end", "breakStartTime", "breakEndTime")
 * @param {dayjs} minStartTime - The minimum start time (e.g., dayjs('03:00', 'HH:mm'))
 * @returns {string || boolean} The error message if the value is invalid, true otherwise
 */
function _validate(field, v, minStartTime) {
  if (!parseTime(v)?.isValid()) return text('invalidTime')
  return getFieldValidationResult(field, this, minStartTime)
}

function getFieldValidationResult(field, ctx, minStartTime) {
  const rules = buildRules(ctx, minStartTime)
  const fieldRules = rules[field] || []

  // Run each validation rule and find the first error
  const firstError = fieldRules
    .map((rule) => rule())
    .find((result) => result !== true)

  return firstError || true
}

function buildRules(ctx, minStartTime) {
  const start = parseTime(ctx.start)
  const end = parseTime(ctx.end)
  const breakStart = parseTime(ctx.breakStartTime)
  const breakEnd = parseTime(ctx.breakEndTime)

  // Helper to get a display value for a time or fallback to field label
  function displayTimeOrField(time, field) {
    return time.isValid()
      ? time.format(window.innerWidth < 1900 ? hmm : hmma)
      : FIELD_LABELS[field]
  }

  return {
    start: [
      () =>
        assertOrMsg(
          start.isAfter(minStartTime),
          'mustBeAfter',
          displayTimeOrField(minStartTime, 'start')
        ),
      () =>
        assertOrMsg(
          end.isAfter(start),
          'mustBeBefore',
          displayTimeOrField(end, 'end')
        ),
    ],
    end: [
      () =>
        assertOrMsg(
          end.isAfter(start),
          'mustBeAfter',
          displayTimeOrField(start, 'start')
        ),
    ],
    breakStartTime: [
      () =>
        assertOrMsg(
          breakStart.isAfter(start) && breakStart.isBefore(end),
          'mustBeBetween',
          displayTimeOrField(start, 'start'),
          displayTimeOrField(end, 'end')
        ),
      () =>
        assertOrMsg(
          breakStart.isBefore(breakEnd),
          'mustBeBefore',
          displayTimeOrField(breakEnd, 'breakEndTime')
        ),
    ],
    breakEndTime: [
      () =>
        assertOrMsg(
          breakEnd.isAfter(start) && breakEnd.isBefore(end),
          'mustBeBetween',
          displayTimeOrField(start, 'start'),
          displayTimeOrField(end, 'end')
        ),
      () =>
        assertOrMsg(
          breakEnd.isAfter(breakStart),
          'mustBeAfter',
          displayTimeOrField(breakStart, 'breakStartTime')
        ),
    ],
  }
}
